import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as DurationKeys from '../reactQueryKeys/durationRoutes'
import * as RouteKeys from '../reactQueryKeys/routes'
import polyline from '@mapbox/polyline'
import type { TravelMode, RouteSummary } from 'tripPlanner/api/routes/getRouteInfo'
import { getRouteInfo } from 'tripPlanner/api/routes/getRouteInfo'

type DurationSummary = Pick<RouteSummary, 'duration' | 'distance' | 'travelMode'>

export const useDurationBetweenTwoPoints = (
  origin?: { lat: number, lng: number },
  destination?: { lat: number, lng: number },
  travelMode?: TravelMode,
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient()
  const enabled = (options?.enabled ?? true) && !!origin && !!destination

  return useQuery<DurationSummary | undefined>({
    queryKey: DurationKeys.durationRoutesQueryKey(origin, destination, travelMode),
    enabled,
    staleTime: 10 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    initialData: () => queryClient.getQueryData<DurationSummary>(
      DurationKeys.durationRoutesQueryKey(origin, destination, travelMode),
    ),
    initialDataUpdatedAt: () => Date.now(),
    queryFn: async() => {
      if (!origin || !destination) return undefined

      const summary = await getRouteInfo(origin, destination, travelMode)
      if (!summary) return undefined

      if (summary.encodedPolyline) {
        const decoded = polyline
          .decode(summary.encodedPolyline)
          .map(([lat, lng]) => ({ lat, lng }))
        queryClient.setQueryData(
          RouteKeys.routeDirectionsQueryKey(origin, destination, summary.travelMode),
          decoded,
        )
      }

      const result = {
        duration: summary.duration,
        distance: summary.distance,
        travelMode: summary.travelMode,
      }

      queryClient.setQueryData(
        DurationKeys.durationRoutesQueryKey(origin, destination, summary.travelMode),
        result,
      )

      return result
    },
  })
}
